<template>
  <div v-if="active" class="impersonation-banner" role="status">
    <div class="impersonation-banner__inner">
      <BaseIcon name="exclamation-triangle" size="14" />
      <div class="impersonation-banner__copy">
        <strong>{{ $t('platform.impersonation.banner_title') }}</strong>
        <span v-if="label">— {{ label }}</span>
      </div>
      <button
        type="button"
        class="impersonation-banner__btn"
        :disabled="exiting"
        @click="exit"
      >
        <BaseIcon name="arrow" size="12" rotate="180" />
        <span>
          {{ exiting ? $t('platform.impersonation.exiting') : $t('platform.impersonation.exit') }}
        </span>
      </button>
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted, onUnmounted, getCurrentInstance } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import {
    isImpersonating,
    impersonatingUserLabel,
    endImpersonation,
    IMPERSONATION_KEYS,
  } from '@/utils/impersonation';
  import { useToastStore } from '@/store/toastStore';

  /**
   * Renders a dismiss-only banner whenever sessionStorage indicates we're
   * impersonating another user. Mounted globally from App.vue so it shows
   * up across the entire app, not just inside /platform/*.
   */
  export default {
    name: 'ImpersonationBanner',
    components: { BaseIcon },
    setup() {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const toastStore = useToastStore();

      const active = ref(false);
      const label = ref(null);
      const exiting = ref(false);

      const refresh = () => {
        active.value = isImpersonating();
        label.value = impersonatingUserLabel();
      };

      const handleStorageEvent = (event) => {
        if (!event?.key) return;
        if (Object.values(IMPERSONATION_KEYS).includes(event.key)) {
          refresh();
        }
      };

      onMounted(() => {
        refresh();
        window.addEventListener('storage', handleStorageEvent);
      });

      onUnmounted(() => {
        window.removeEventListener('storage', handleStorageEvent);
      });

      const exit = async () => {
        if (exiting.value) return;
        exiting.value = true;
        try {
          await endImpersonation();
        } catch (error) {
          console.error('Failed to end impersonation:', error);
          toastStore.addToast({
            type: 'error',
            message: $t('platform.impersonation.exit_failed'),
          });
          exiting.value = false;
        }
      };

      return { active, label: computed(() => label.value), exiting, exit, $t };
    },
  };
</script>

<style scoped lang="scss">
  .impersonation-banner {
    background: linear-gradient(135deg, #f59e0b, #b45309);
    color: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);

    &__inner {
      max-width: 1500px;
      margin: 0 auto;
      padding: 0.5rem 1rem;
      display: flex;
      align-items: center;
      gap: 0.75rem;

      .icon {
        flex-shrink: 0;
      }
    }

    &__copy {
      flex: 1;
      min-width: 0;
      font-size: 0.8125rem;
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem;
      align-items: baseline;
    }

    &__btn {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.375rem 0.75rem;
      background: rgba(0, 0, 0, 0.22);
      border: 1px solid rgba(255, 255, 255, 0.3);
      color: #fff;
      border-radius: 0.5rem;
      font-size: 0.8125rem;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 0.15s ease;

      .icon {
        color: inherit;
      }

      &:hover:not(:disabled) {
        background: rgba(0, 0, 0, 0.32);
      }

      &:disabled {
        opacity: 0.7;
        cursor: progress;
      }
    }
  }
</style>
